/**
 * PricingCTA Component - HI-SI Landing Page
 * HISIP-016: Pricing section and subscription CTAs
 */

import { motion } from 'framer-motion';
import { 
  Check, 
  Zap, 
  Crown, 
  Infinity,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { cn } from '@/utils/cn';

interface PricingCTAProps {
  onStartCreating: () => void;
  onSeePricing: () => void;
}

const PRICING_TIERS = [
  {
    name: 'Creator',
    price: '$29',
    period: 'per month',
    description: 'Perfect for individual creators and small projects',
    features: [
      '10 videos per month',
      'All 7 AI stages',
      'HD video output',
      'Basic analytics',
      'Community support'
    ],
    icon: Zap,
    gradient: 'from-purple-500 to-purple-600',
    borderColor: 'border-purple-500/30',
    popular: false
  },
  {
    name: 'Professional',
    price: '$89',
    period: 'per month',
    description: 'For agencies and growing content teams',
    features: [
      'Unlimited videos',
      'Priority processing',
      '4K video output',
      'Advanced analytics',
      'Custom branding',
      'API access',
      'Priority support'
    ],
    icon: Crown,
    gradient: 'from-cyan-500 to-blue-600',
    borderColor: 'border-cyan-500/30',
    popular: true
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: 'pricing',
    description: 'Tailored solutions for large organizations',
    features: [
      'Volume discounts',
      'Custom AI models',
      'White-label solution',
      'Dedicated support',
      'SLA guarantees',
      'On-premise deployment'
    ],
    icon: Infinity,
    gradient: 'from-emerald-500 to-green-600',
    borderColor: 'border-emerald-500/30',
    popular: false
  }
];

export default function PricingCTA({ onStartCreating, onSeePricing }: PricingCTAProps) {
  return (
    <section className="relative py-20 overflow-hidden" id="pricing">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-cyan-900/10 to-black" />
        
        {/* Radial gradients */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full h-full">
          <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-purple-500/5 blur-3xl" />
          <div className="absolute top-40 right-1/4 w-96 h-96 rounded-full bg-cyan-500/5 blur-3xl" />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-light text-white mb-6">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Simple, Transparent Pricing
            </span>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
            Choose the plan that fits your creative needs. All plans include access to our 
            full seven-stage AI pipeline with unlimited iterations until perfection.
          </p>
        </motion.div>

        {/* Pricing Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {PRICING_TIERS.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group relative"
            >
              {/* Popular Badge */}
              {tier.popular && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10"
                >
                  <div className="px-4 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-sm font-medium flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Most Popular
                  </div>
                </motion.div>
              )}

              {/* Pricing Card */}
              <div className={cn(
                "h-full p-8 rounded-2xl backdrop-blur-xl border transition-all duration-300",
                "bg-black/30",
                tier.popular 
                  ? "border-cyan-500/50 shadow-xl shadow-cyan-500/10" 
                  : "border-white/10 hover:border-purple-500/30",
                "group-hover:bg-black/50",
                tier.popular 
                  ? "scale-105" 
                  : "group-hover:shadow-xl group-hover:shadow-purple-500/10"
              )}>
                {/* Tier Icon and Name */}
                <div className="text-center mb-8">
                  <div className={cn(
                    "inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4",
                    "bg-gradient-to-br", tier.gradient.replace('to-', 'to-').replace(/\d+$/, '/20'),
                    "border", tier.borderColor,
                    "group-hover:scale-110 transition-transform duration-300"
                  )}>
                    <tier.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-light text-white mb-2">{tier.name}</h3>
                  <p className="text-white/60 text-sm">{tier.description}</p>
                </div>

                {/* Pricing */}
                <div className="text-center mb-8">
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-4xl font-light text-white">{tier.price}</span>
                    <span className="text-white/60 ml-2">/{tier.period}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <motion.div
                      key={featureIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.8 + index * 0.1 + featureIndex * 0.05 }}
                      viewport={{ once: true }}
                      className="flex items-center gap-3"
                    >
                      <div className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0",
                        "bg-gradient-to-br", tier.gradient.replace(/\d+$/, '/20'),
                        tier.borderColor, "border"
                      )}>
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-white/80 text-sm">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                {/* CTA Button */}
                <motion.button
                  onClick={tier.name === 'Enterprise' ? onSeePricing : onStartCreating}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    "w-full py-3 px-6 rounded-xl font-medium transition-all duration-300",
                    "flex items-center justify-center gap-2",
                    tier.popular
                      ? cn(
                          "bg-gradient-to-r", tier.gradient,
                          "text-white shadow-lg hover:shadow-xl",
                          "hover:shadow-cyan-500/25"
                        )
                      : "bg-white/10 text-white hover:bg-white/20 border border-white/20"
                  )}
                  style={tier.popular ? {
                    boxShadow: '0 0 30px rgba(6, 182, 212, 0.3)'
                  } : {}}
                >
                  {tier.name === 'Enterprise' ? 'Contact Sales' : 'Start Creating'}
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-8 px-8 py-4 rounded-full bg-black/30 backdrop-blur-xl border border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-white/80 text-sm">99.9% Uptime</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-cyan-400 rounded-full" />
              <span className="text-white/80 text-sm">7-Day Free Trial</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-400 rounded-full" />
              <span className="text-white/80 text-sm">Cancel Anytime</span>
            </div>
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <h3 className="text-2xl font-light text-white mb-4">
            Ready to Transform Your Video Creation?
          </h3>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Join thousands of creators who are already using HI-SI to produce 
            professional videos in minutes, not hours.
          </p>
          
          <motion.button
            onClick={onStartCreating}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={cn(
              "px-12 py-4 rounded-xl font-medium text-lg",
              "bg-gradient-to-r from-purple-600 to-cyan-600",
              "text-white shadow-xl hover:shadow-2xl hover:shadow-purple-500/25",
              "transition-all duration-300",
              "flex items-center gap-3 mx-auto"
            )}
            style={{
              boxShadow: '0 0 40px rgba(168, 85, 247, 0.3)'
            }}
          >
            <Zap className="w-5 h-5" />
            Start Your Free Trial
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>

      </div>
    </section>
  );
}